import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import netSuiteLogo from '../../assets/logos/netsuite-logo.svg';
import openAILogo from '../../assets/logos/openai-logo.svg';
import anthropicLogo from '../../assets/logos/anthropic-logo.svg';
import { 
  faNetworkWired, 
  faPlus, 
  faEdit, 
  faTrash, 
  faCheckCircle,
  faSearch,
  faTh,
  faList,
  faEye
} from '@fortawesome/free-solid-svg-icons';


import MainLayout from '../Layout/MainLayout';
import { connectorAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import AddConnectorModal from './AddConnectorModal';
import NetSuiteConnectorForm from './NetSuiteConnectorForm';
import OpenAIConnectorForm from './OpenAIConnectorForm';
import AnthropicConnectorForm from './AnthropicConnectorForm';
import SalesforceConnectorForm from './SalesforceConnectorForm';
import MicrosoftConnectorForm from './MicrosoftConnectorForm';
import ViewConnectorModal from './ViewConnectorModal';

const ConnectorManagement = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNetSuiteForm, setShowNetSuiteForm] = useState(false);
  const [showOpenAIForm, setShowOpenAIForm] = useState(false);
  const [showAnthropicForm, setShowAnthropicForm] = useState(false);
  const [showSalesforceForm, setShowSalesforceForm] = useState(false);
  const [showMicrosoftForm, setShowMicrosoftForm] = useState(false);

  // Edit states
  const [editingConnector, setEditingConnector] = useState(null);
  const [showEditNetSuiteForm, setShowEditNetSuiteForm] = useState(false);
  const [showEditOpenAIForm, setShowEditOpenAIForm] = useState(false);
  const [showEditAnthropicForm, setShowEditAnthropicForm] = useState(false);
  const [showEditSalesforceForm, setShowEditSalesforceForm] = useState(false);
  const [showEditMicrosoftForm, setShowEditMicrosoftForm] = useState(false);

  // View state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingConnector, setViewingConnector] = useState(null);

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    try {
      // Get both available connectors and user credentials
      const [availableResponse, credentialsResponse] = await Promise.all([
        connectorAPI.getConnectors(),
        connectorAPI.getUserConnectors()
      ]);

      if (availableResponse.success && credentialsResponse.success) {
        console.log('📡 Raw API response:', credentialsResponse.connectors);
        // Transform user credentials into connector format with instance info
        const userConnectors = credentialsResponse.connectors.map((cred) => {
          const transformed = {
            id: cred.id || `${cred.connector_name}-${cred.instance_name}`,
            name: cred.connector_name,
            instance_name: cred.instance_name || 'default',
            service_type: cred.auth_type,
            status: cred.status || 'active',
            is_selected: Boolean(cred.is_selected),
            credentials: {}, // Credentials not returned for security
            created_at: cred.created_at,
            last_tested: cred.updated_at || cred.created_at,
            metadata: {}
          };
          console.log(`🔄 Connector ${transformed.name} (${transformed.instance_name}): is_selected=${transformed.is_selected}`);
          return transformed;
        });

        console.log('✅ Setting connectors state:', userConnectors);
        // Force new array reference and increment refresh key to trigger re-render
        setConnectors([...userConnectors]);
        setRefreshKey(prev => prev + 1);
      } else {
        setConnectors([]);
      }
    } catch (error) {
      console.error('Failed to load connectors:', error);
      // Set empty array for now - real connectors will be created via forms
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (connectorName) => {
    try {
      toast.loading('Testing connection...', { id: 'test-connection' });
      const response = await connectorAPI.testConnection(connectorName);

      if (response.success) {
        toast.success(response.message || 'Connection test successful!', { id: 'test-connection' });
        loadConnectors(); // Refresh to get updated status
      } else {
        toast.error(response.message || 'Connection test failed', { id: 'test-connection' });
      }
    } catch (error) {
      console.error('Connection test error:', error);
      const errorMessage = error.response?.data?.detail?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Connection test failed';
      toast.error(errorMessage, { id: 'test-connection' });
    }
  };

  const handleSetSelectedInstance = async (connectorName, instanceName) => {
    try {
      toast.loading('Setting as active...', { id: 'set-selected' });
      const response = await connectorAPI.setSelectedInstance(connectorName, instanceName);

      if (response.success) {
        toast.success(`${instanceName} is now the active connector`, { id: 'set-selected' });
        // Update state directly for immediate UI feedback
        setConnectors(prevConnectors =>
          prevConnectors.map(c => ({
            ...c,
            is_selected: c.name === connectorName && c.instance_name === instanceName
              ? true
              : c.name === connectorName
                ? false  // Deselect other instances of same connector
                : c.is_selected  // Keep other connectors unchanged
          }))
        );
        setRefreshKey(prev => prev + 1);
      } else {
        toast.error('Failed to set active connector', { id: 'set-selected' });
      }
    } catch (error) {
      console.error('Set selected instance error:', error);
      toast.error('Failed to set active connector', { id: 'set-selected' });
    }
  };

  const handleDeselectInstance = async (connectorName, instanceName) => {
    try {
      toast.loading('Deactivating connector...', { id: 'deselect' });
      const response = await connectorAPI.deselectInstance(connectorName, instanceName);

      if (response.success) {
        toast.success(`${instanceName} has been deactivated`, { id: 'deselect' });
        // Update state directly for immediate UI feedback
        setConnectors(prevConnectors =>
          prevConnectors.map(c => ({
            ...c,
            is_selected: c.name === connectorName && c.instance_name === instanceName
              ? false
              : c.is_selected
          }))
        );
        setRefreshKey(prev => prev + 1);
      } else {
        toast.error('Failed to deactivate connector', { id: 'deselect' });
      }
    } catch (error) {
      console.error('Deselect instance error:', error);
      toast.error('Failed to deactivate connector', { id: 'deselect' });
    }
  };

  const handleDeleteConnector = async (connectorName) => {
    if (!window.confirm('Are you sure you want to delete this connector?')) {
      return;
    }

    try {
      const response = await connectorAPI.deleteConnector(connectorName);
      if (response.success) {
        toast.success('Connector deleted successfully');
        loadConnectors();
      }
    } catch (error) {
      toast.error('Failed to delete connector');
      console.error('Connector deletion error:', error);
    }
  };

  const handleAddConnector = () => {
    setShowAddModal(true);
  };

  const handleSelectConnectorType = (type) => {
    setShowAddModal(false);

    switch (type) {
      case 'netsuite':
        setShowNetSuiteForm(true);
        break;
      case 'openai':
        setShowOpenAIForm(true);
        break;
      case 'anthropic':
        setShowAnthropicForm(true);
        break;
      case 'salesforce':
        setShowSalesforceForm(true);
        break;
      case 'microsoft':
        setShowMicrosoftForm(true);
        break;
      default:
        console.error('Unknown connector type:', type);
    }
  };

  const handleSaveConnector = async (connectorData) => {
    try {
      const response = await connectorAPI.createConnector(connectorData);
      if (response.success) {
        loadConnectors(); // Refresh the list
        return response;
      } else {
        throw new Error(response.message || 'Failed to create connector');
      }
    } catch (error) {
      console.error('Error creating connector:', error);
      throw error;
    }
  };

  const handleViewConnector = (connector) => {
    setViewingConnector(connector);
    setShowViewModal(true);
  };

  const handleGetConnectorDetails = async (connectorName) => {
    try {
      const response = await connectorAPI.getConnector(connectorName);
      return response;
    } catch (error) {
      console.error('Failed to load connector details:', error);
      throw error;
    }
  };

  const handleEditConnector = async (connector) => {
    try {
      // Load full connector details including credentials for editing
      const fullConnectorData = await connectorAPI.getConnector(connector.name);
      
      if (fullConnectorData.success) {
        // Merge the full data with the connector info
        const enhancedConnector = {
          ...connector,
          credentials: fullConnectorData.credentials,
          metadata: fullConnectorData.metadata
        };
        
        setEditingConnector(enhancedConnector);
        
        // Check service_type first, fall back to auth_type, then metadata.service_type
        const connectorType = connector.service_type || connector.auth_type || connector.metadata?.service_type;
        switch (connectorType) {
          case 'oauth1': // NetSuite uses oauth1
            setShowEditNetSuiteForm(true);
            break;
          case 'api_key':
            if (connector.name.toLowerCase().includes('openai')) {
              setShowEditOpenAIForm(true);
            } else if (connector.name.toLowerCase().includes('anthropic')) {
              setShowEditAnthropicForm(true);
            } else {
              // Default to OpenAI form for generic api_key
              setShowEditOpenAIForm(true);
            }
            break;
          case 'openai': // OpenAI service type
            setShowEditOpenAIForm(true);
            break;
          case 'anthropic': // Anthropic service type
            setShowEditAnthropicForm(true);
            break;
          case 'salesforce': // Salesforce
          case 'oauth2': // Salesforce OAuth2
          case 'password': // Salesforce username/password
            setShowEditSalesforceForm(true);
            break;
          case 'microsoft': // Microsoft 365
          case 'client_credentials': // Microsoft or Salesforce Client Credentials
            // Check connector name to determine which form to show
            if (connector.name.toLowerCase().includes('microsoft') ||
                fullConnectorData.credentials?.tenant_id) {
              setShowEditMicrosoftForm(true);
            } else {
              setShowEditSalesforceForm(true);
            }
            break;
          default:
            toast.error('Edit not supported for this connector type');
        }
      }
    } catch (error) {
      toast.error('Failed to load connector data for editing');
      console.error('Edit connector error:', error);
    }
  };

  const handleUpdateConnector = async (connectorData) => {
    try {
      if (!editingConnector) {
        throw new Error('No connector selected for editing');
      }
      
      const response = await connectorAPI.updateConnector(editingConnector.name, connectorData);
      if (response.success) {
        loadConnectors(); // Refresh the list
        setEditingConnector(null);
        return response;
      } else {
        throw new Error(response.message || 'Failed to update connector');
      }
    } catch (error) {
      console.error('Error updating connector:', error);
      throw error;
    }
  };


  const filteredConnectors = connectors.filter(connector =>
    connector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connector.service_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getConnectorLogo = (serviceType) => {
    switch (serviceType) {
      case 'netsuite':
      case 'oauth1':
        return netSuiteLogo;
      case 'openai':
        return openAILogo;
      case 'anthropic':
        return anthropicLogo;
      case 'salesforce':
      case 'oauth2':
      case 'password':
        return null; // Salesforce uses fallback icon for now
      case 'microsoft':
      case 'client_credentials':
        return null; // Microsoft uses fallback icon for now
      default:
        return null;
    }
  };

  const getConnectorName = (serviceType) => {
    switch (serviceType) {
      case 'netsuite':
      case 'oauth1':
        return 'NetSuite';
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      case 'salesforce':
      case 'oauth2':
      case 'password':
        return 'Salesforce';
      case 'microsoft':
        return 'Microsoft 365';
      case 'client_credentials':
        return 'Cloud Service';
      default:
        return serviceType;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };


  if (loading) {
    return (
      <MainLayout title="Connector Management">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading connectors...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Connector Management">
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={handleAddConnector}
            className="btn btn-primary w-full sm:w-auto"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Connector
          </button>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative flex-1 sm:flex-none">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full sm:w-64"
                placeholder="Search connectors..."
              />
            </div>

            {/* View Toggle */}
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 self-end sm:self-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-l-lg transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faTh} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-r-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faList} />
              </button>
            </div>
          </div>
        </div>

        {/* Connectors Grid/List */}
        {filteredConnectors.length > 0 ? (
          <div key={`connectors-${refreshKey}`} className={`${viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-4'}`}>
            {filteredConnectors.map((connector, index) => (
              <motion.div
                key={`${connector.id}-${connector.is_selected}-${refreshKey}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card hover:shadow-lg transition-all duration-200"
              >
                {viewMode === 'grid' ? (
                  // Grid View
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                          {getConnectorLogo(connector.service_type) ? (
                            <img
                              src={getConnectorLogo(connector.service_type)}
                              alt={`${getConnectorName(connector.service_type)} Logo`}
                              className="w-10 h-6 object-contain"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center text-white text-xl">
                              🔗
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{connector.name}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {getConnectorName(connector.service_type)}
                            {connector.instance_name && connector.instance_name !== 'default' && (
                              <span className="ml-1 text-indigo-600 dark:text-indigo-400">
                                ({connector.instance_name})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(connector.status)}`}>
                          {connector.status}
                        </span>
                      </div>
                    </div>

                    {/* Connection Status */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Connection Status
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Authentication:</span>
                          <span className="text-xs text-gray-900 dark:text-white font-mono capitalize">
                            {connector.service_type}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Instance:</span>
                          <span className="text-xs text-gray-900 dark:text-white font-mono">
                            {connector.instance_name || 'default'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Credentials:</span>
                          <span className="text-xs text-green-600 dark:text-green-400">
                            ✓ Configured
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Created:</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatTimeAgo(connector.created_at)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last tested:</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatTimeAgo(connector.last_tested)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleTestConnection(connector.name)}
                        className="btn btn-primary flex-1 min-w-[80px] text-xs sm:text-sm py-2"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                        Test
                      </button>
                      <button
                        onClick={() => {
                          if (connector.is_selected) {
                            console.log(`🖱️ Clicking Deselect for: ${connector.name} / ${connector.instance_name}`);
                            handleDeselectInstance(connector.name, connector.instance_name || 'default');
                          } else {
                            console.log(`🖱️ Clicking Select for: ${connector.name} / ${connector.instance_name}`);
                            handleSetSelectedInstance(connector.name, connector.instance_name || 'default');
                          }
                        }}
                        className={`btn px-3 py-2 text-xs sm:text-sm font-medium transition-all ${
                          connector.is_selected
                            ? 'bg-green-600 hover:bg-red-600 text-white cursor-pointer'
                            : 'bg-gray-100 hover:bg-indigo-600 text-gray-700 hover:text-white dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-indigo-600 cursor-pointer'
                        }`}
                        title={connector.is_selected ? 'Click to Deactivate' : 'Click to Activate'}
                      >
                        {connector.is_selected ? 'Deselect' : 'Select'}
                      </button>
                      <button
                        onClick={() => handleViewConnector(connector)}
                        className="btn btn-secondary px-2 sm:px-3 py-2"
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => handleEditConnector(connector)}
                        className="btn btn-secondary px-2 sm:px-3 py-2"
                        title="Edit Connector"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDeleteConnector(connector.name)}
                        className="btn btn-danger px-2 sm:px-3 py-2"
                        title="Delete Connector"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // List View
                  <div className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex items-center space-x-3 sm:space-x-4">
                        <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                          {getConnectorLogo(connector.service_type) ? (
                            <img
                              src={getConnectorLogo(connector.service_type)}
                              alt={`${getConnectorName(connector.service_type)} Logo`}
                              className="w-8 h-5 object-contain"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded flex items-center justify-center text-white text-sm">
                              🔗
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{connector.name}</h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm truncate">
                            {getConnectorName(connector.service_type)} • Last tested {formatTimeAgo(connector.last_tested)}
                          </p>
                        </div>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap sm:hidden ${getStatusColor(connector.status)}`}>
                          {connector.status}
                        </span>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-4">
                        <span className={`hidden sm:inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(connector.status)}`}>
                          {connector.status}
                        </span>
                        <div className="flex space-x-1 sm:space-x-2">
                          <button
                            onClick={() => handleTestConnection(connector.name)}
                            className="btn btn-primary text-xs sm:text-sm px-2 sm:px-3 py-1"
                            title="Test Connection"
                          >
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </button>
                          <button
                            onClick={() => {
                              if (connector.is_selected) {
                                console.log(`🖱️ Clicking Deselect for: ${connector.name} / ${connector.instance_name}`);
                                handleDeselectInstance(connector.name, connector.instance_name || 'default');
                              } else {
                                console.log(`🖱️ Clicking Select for: ${connector.name} / ${connector.instance_name}`);
                                handleSetSelectedInstance(connector.name, connector.instance_name || 'default');
                              }
                            }}
                            className={`btn text-xs sm:text-sm px-2 sm:px-3 py-1 font-medium transition-all ${
                              connector.is_selected
                                ? 'bg-green-600 hover:bg-red-600 text-white cursor-pointer'
                                : 'bg-gray-100 hover:bg-indigo-600 text-gray-700 hover:text-white dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-indigo-600 cursor-pointer'
                            }`}
                            title={connector.is_selected ? 'Click to Deactivate' : 'Click to Activate'}
                          >
                            {connector.is_selected ? 'Deselect' : 'Select'}
                          </button>
                          <button
                            onClick={() => handleViewConnector(connector)}
                            className="btn btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            onClick={() => handleEditConnector(connector)}
                            className="btn btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1"
                            title="Edit Connector"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteConnector(connector.name)}
                            className="btn btn-danger text-xs sm:text-sm px-2 sm:px-3 py-1"
                            title="Delete Connector"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faNetworkWired} className="text-4xl text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No connectors found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm ? 'No connectors match your search criteria.' : 'Get started by adding your first connector.'}
            </p>
            <button 
              onClick={handleAddConnector}
              className="btn btn-primary"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Your First Connector
            </button>
          </div>
        )}
        
        {/* Modals */}
        <ViewConnectorModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setViewingConnector(null);
          }}
          connector={viewingConnector}
          onEdit={handleEditConnector}
          onDelete={handleDeleteConnector}
          onTest={handleTestConnection}
          onRefreshData={handleGetConnectorDetails}
        />

        <AddConnectorModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSelectConnectorType={handleSelectConnectorType}
        />
        
        <NetSuiteConnectorForm
          isOpen={showNetSuiteForm}
          onClose={() => setShowNetSuiteForm(false)}
          onSave={handleSaveConnector}
        />
        
        <OpenAIConnectorForm
          isOpen={showOpenAIForm}
          onClose={() => setShowOpenAIForm(false)}
          onSave={handleSaveConnector}
        />
        
        <AnthropicConnectorForm
          isOpen={showAnthropicForm}
          onClose={() => setShowAnthropicForm(false)}
          onSave={handleSaveConnector}
        />

        <SalesforceConnectorForm
          isOpen={showSalesforceForm}
          onClose={() => setShowSalesforceForm(false)}
          onSave={handleSaveConnector}
        />

        <MicrosoftConnectorForm
          isOpen={showMicrosoftForm}
          onClose={() => setShowMicrosoftForm(false)}
          onSave={handleSaveConnector}
        />

        {/* Edit Modals */}
        <NetSuiteConnectorForm
          isOpen={showEditNetSuiteForm}
          onClose={() => {
            setShowEditNetSuiteForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />
        
        <OpenAIConnectorForm
          isOpen={showEditOpenAIForm}
          onClose={() => {
            setShowEditOpenAIForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />
        
        <AnthropicConnectorForm
          isOpen={showEditAnthropicForm}
          onClose={() => {
            setShowEditAnthropicForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />

        <SalesforceConnectorForm
          isOpen={showEditSalesforceForm}
          onClose={() => {
            setShowEditSalesforceForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />

        <MicrosoftConnectorForm
          isOpen={showEditMicrosoftForm}
          onClose={() => {
            setShowEditMicrosoftForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />
      </div>
    </MainLayout>
  );
};

export default ConnectorManagement;